// Is this address somewhere the graph can actually route?
//
// A dispatcher composes a run in a browser, against a search index that
// covers a continent, and mints a ticket against a routing graph that covers
// one region. Nothing connected those two facts. So a stop in Sainte-Thérèse
// could be added to a plan built on a graph that has never heard of it, and
// the ticket minted from it was well-formed, signed, and unroutable.
//
// The driver's phone found out first, hours later, in a van. It reported the
// only thing it could see — an epoch that did not match — which named the
// symptom and not the cause. This asks the question at the moment the stop is
// added, where the answer is still free and the dispatcher is still looking at
// the address.
//
// The question is deliberately the weakest useful one: **does the graph have
// any road near this point.** Not "can a van reach it", which needs a route
// from a depot that may not be chosen yet, and not "is it inside the bbox",
// which a graph's bounding box answers yes to for a lake. A stop with no road
// within a couple of hundred metres cannot be delivered to by any vehicle on
// any graph, and that is the case worth refusing.

import type { LatLon, RouteEngine } from "./engine.js";

/** Metres of latitude per degree. Close enough anywhere; the tolerance is coarse. */
const METRES_PER_DEGREE = 111_320;

/** How far from a stop a road may be and still count as serving it. */
export const DEFAULT_ROAD_MARGIN_METRES = 250;

/** What the graph knows about one stop. */
export interface StopCoverage {
  /** The graph has at least one road within the margin. */
  covered: boolean;
  /**
   * The question could not be answered — the graph did not load, the request
   * failed. Explicitly **not** the same as "not covered": refusing a stop
   * because the network blinked would be worse than the bug this prevents.
   */
  unknown?: string;
}

/**
 * A box of `margin` metres around a point.
 *
 * Longitude degrees shrink with latitude, so the correction matters at the
 * latitudes this actually runs at — at 60°N an uncorrected box is half as wide
 * as it claims, which would refuse stops on real streets in northern Québec.
 */
export function boxAround(point: LatLon, margin = DEFAULT_ROAD_MARGIN_METRES) {
  const dLat = margin / METRES_PER_DEGREE;
  // Guard the pole: cos(90°) is 0, and dividing by it is an infinite box.
  const shrink = Math.max(0.01, Math.cos((point.lat * Math.PI) / 180));
  const dLon = margin / (METRES_PER_DEGREE * shrink);
  return {
    minLat: point.lat - dLat,
    maxLat: point.lat + dLat,
    minLon: point.lon - dLon,
    maxLon: point.lon + dLon
  };
}

/**
 * Whether the routing graph has a road serving this point.
 *
 * Never throws. A failure is reported as [StopCoverage.unknown] and the stop
 * is left alone: the dispatcher's plan is not the place to enforce a network
 * condition.
 */
export async function coverageOf(
  engine: Pick<RouteEngine, "roadsIn">,
  point: LatLon,
  margin = DEFAULT_ROAD_MARGIN_METRES
): Promise<StopCoverage> {
  try {
    // One leaf is enough: the question is "any road at all", and asking for
    // more would page in tiles to answer a question already answered by the
    // first one.
    const { roads } = await engine.roadsIn(boxAround(point, margin), { maxLeaves: 1 });
    return { covered: roads.length > 0 };
  } catch (error) {
    return {
      covered: true,
      unknown: error instanceof Error ? error.message : "The map could not be asked about this address."
    };
  }
}

/** One stop that the graph cannot serve. */
export interface OffGraphStop<T> {
  stop: T;
  index: number;
}

/**
 * Every stop in a plan the graph has no road for.
 *
 * Sequential rather than parallel, and deliberately so: each call may page in
 * a leaf over the network, and a fifty-stop run fanned out at once is the
 * shape that already took a browser down once in this codebase. A dispatcher
 * adds stops one at a time anyway — the batch path exists for a plan pasted in
 * whole, where being unhurried is free.
 */
export async function offGraphStops<T extends LatLon>(
  engine: Pick<RouteEngine, "roadsIn">,
  stops: readonly T[],
  margin = DEFAULT_ROAD_MARGIN_METRES
): Promise<OffGraphStop<T>[]> {
  const out: OffGraphStop<T>[] = [];
  for (const [index, stop] of stops.entries()) {
    const coverage = await coverageOf(engine, stop, margin);
    // An unanswered question is not a refusal. `covered` is already true in
    // that case; this only spells out why nothing is collected.
    if (!coverage.covered && !coverage.unknown) out.push({ stop, index });
  }
  return out;
}

/**
 * What to tell the dispatcher, naming the address rather than the geometry.
 *
 * Says which region the graph covers when that is known, because "outside the
 * map" invites the reply "which map?" — and the answer is usually that they
 * have the wrong region loaded, not that the address is wrong.
 */
export function offGraphMessage(label: string, regionName?: string): string {
  const where = label.trim() || "That address";
  return regionName
    ? `${where} is outside the ${regionName} map, so no route can be planned to it. `
      + "Load the region that covers it, or check the address."
    : `${where} is outside the loaded map, so no route can be planned to it. `
      + "Load the region that covers it, or check the address.";
}
